import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../../hooks/useBackHandler';
import { getMonsterImgPath } from '../../utils/monster';

/**
 * **방덱 × 공덱 메모** (사용자 2026-10-15) — 방덱 검색 결과의 공덱 줄마다 [메모] 단추. 누르면 창이 열려 메모를 보고,
 * 로그인했으면 고쳐 저장한다. 서버 `/api/deck-memos` (열쇠는 서버가 몹 조합으로 만든다 — 리더 + 나머지 순서 무관, 콜라보 짝 같음).
 */
type Mob = { name: string; attribute: string };
export interface DeckMemo { text: string; updatedByName?: string; updatedAt?: string }

const plain = (t: any[]): Mob[] => (t || []).map((m: any) => ({ name: m?.name, attribute: m?.attribute }));

/** 공덱 여러 개의 메모를 한 번에 받는다. `teams` 는 열쇠(묶음 id 등) → 공덱 */
export function useDeckMemos(defenseTeam: any[] | null | undefined, teams: Array<{ key: string; team: any[] }>) {
    const [memos, setMemos] = useState<Record<string, DeckMemo | null>>({});
    const sig = JSON.stringify([plain(defenseTeam || []), teams.map((t) => [t.key, plain(t.team)])]);
    useEffect(() => {
        if (!defenseTeam || defenseTeam.length !== 3 || !teams.length) { setMemos({}); return; }
        let gone = false;
        fetch('/api/deck-memos/list', {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ defenseTeam: plain(defenseTeam), attackTeams: teams.map((t) => plain(t.team)) })
        })
            .then((r) => r.json())
            .then((j) => {
                if (gone || !j?.success) return;
                const out: Record<string, DeckMemo | null> = {};
                teams.forEach((t, i) => { out[t.key] = j.memos?.[i] || null; });
                setMemos(out);
            })
            .catch(() => {});
        return () => { gone = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);
    const setOne = (key: string, memo: DeckMemo | null) => setMemos((m) => ({ ...m, [key]: memo }));
    return { memos, setOne };
}

/** 줄 안의 [메모] 단추 — 메모가 있으면 노란 칠. 줄을 누른 것으로 번지지 않게 막는다 */
export function DeckMemoButton({ memo, onClick, isMobile }: { memo?: DeckMemo | null; onClick: () => void; isMobile?: boolean }) {
    const has = !!memo?.text;
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            title={has ? memo!.text : '메모 남기기'}
            style={{
                flexShrink: 0, cursor: 'pointer', fontWeight: 800, whiteSpace: 'nowrap',
                fontSize: isMobile ? 11 : 12, padding: isMobile ? '5px 8px' : '6px 12px', borderRadius: 8,
                border: has ? '1.5px solid #f0b400' : '1.5px dashed rgba(102, 126, 234, 0.45)',
                background: has ? 'linear-gradient(135deg, #fff6cc 0%, #ffe68a 100%)' : 'rgba(102, 126, 234, 0.05)',
                color: has ? '#8a6100' : '#667eea'
            }}
        >메모{has ? ' ✎' : ' +'}</button>
    );
}

function MobIcon({ m, mapping, size }: { m: Mob; mapping: any; size: number }) {
    const { imgPath, displayName } = getMonsterImgPath(mapping, m);
    return imgPath
        ? <img src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`} alt={displayName} title={displayName} style={{ width: size, height: size, borderRadius: size * 0.22, objectFit: 'contain' }} />
        : <span style={{ fontSize: 10, color: '#999' }}>{displayName}</span>;
}

/** 메모 창 — 보기 + (로그인했으면) 고치기·저장. 빈 글로 저장하면 지운다 */
export function DeckMemoModal({ defenseTeam, attackTeam, memo, canEdit, mapping, isMobile, onClose, onSaved }: {
    defenseTeam: any[]; attackTeam: any[]; memo?: DeckMemo | null; canEdit: boolean; mapping: any; isMobile?: boolean;
    onClose: () => void; onSaved: (memo: DeckMemo | null) => void;
}) {
    useBackHandler(true, onClose);
    const [text, setText] = useState(memo?.text || '');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const changed = text !== (memo?.text || '');

    const save = async () => {
        if (busy) return;
        setBusy(true); setErr('');
        try {
            const r = await fetch('/api/deck-memos', {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ defenseTeam: plain(defenseTeam), attackTeam: plain(attackTeam), text })
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.success) throw new Error(j.error || '저장하지 못했습니다');
            onSaved(j.memo || null);
            onClose();
        } catch (e: any) {
            setErr(e.message || '저장하지 못했습니다');
        } finally {
            setBusy(false);
        }
    };

    const icon = isMobile ? 26 : 30;
    return createPortal(
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,10,30,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 8 : 24 }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 18,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '12px 14px' : '14px 18px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 900, fontSize: isMobile ? 14 : 15, color: '#1976d2' }}>공덱</span>
                    {plain(attackTeam).map((m, i) => <MobIcon key={`a${i}`} m={m} mapping={mapping} size={icon} />)}
                    <span style={{ margin: '0 4px', fontSize: 11, fontWeight: 800, color: '#bbb' }}>vs</span>
                    <span style={{ fontWeight: 900, fontSize: isMobile ? 14 : 15, color: '#d32f2f' }}>방덱</span>
                    {plain(defenseTeam).map((m, i) => <MobIcon key={`d${i}`} m={m} mapping={mapping} size={icon} />)}
                    <button onClick={onClose} aria-label="닫기" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', fontSize: 24, fontWeight: 900, color: '#888', cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: isMobile ? '12px 14px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {canEdit ? (
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            maxLength={2000}
                            autoFocus
                            placeholder="이 공덱으로 이 방덱을 칠 때의 메모 (룬·순서·주의할 점 …)"
                            style={{
                                width: '100%', minHeight: isMobile ? 160 : 200, boxSizing: 'border-box', resize: 'vertical',
                                border: '1.5px solid rgba(102, 126, 234, 0.3)', borderRadius: 12, padding: 12,
                                fontSize: isMobile ? 14 : 15, lineHeight: 1.5, fontFamily: 'inherit', outline: 'none'
                            }}
                        />
                    ) : (
                        <div style={{ minHeight: 80, whiteSpace: 'pre-wrap', fontSize: isMobile ? 14 : 15, lineHeight: 1.5, color: memo?.text ? '#333' : '#aaa' }}>
                            {memo?.text || '아직 메모가 없습니다'}
                        </div>
                    )}
                    {memo?.text && (memo.updatedByName || memo.updatedAt) && (
                        <div style={{ fontSize: 11, color: '#999' }}>
                            마지막 저장 {memo.updatedByName || ''}{memo.updatedAt ? ` · ${new Date(memo.updatedAt).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </div>
                    )}
                    {err && <div style={{ color: '#e11d48', fontSize: 12, fontWeight: 700 }}>{err}</div>}
                    {!canEdit && <div style={{ fontSize: 12, color: '#999' }}>로그인하면 메모를 고칠 수 있습니다</div>}
                </div>
                {canEdit && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: isMobile ? '0 14px 14px' : '0 18px 16px' }}>
                        <button onClick={onClose} style={{ border: '1px solid #ddd', background: '#fff', color: '#666', borderRadius: 10, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>취소</button>
                        <button onClick={save} disabled={busy || !changed} style={{
                            border: 'none', borderRadius: 10, padding: '8px 18px', fontWeight: 800, cursor: busy || !changed ? 'default' : 'pointer',
                            background: busy || !changed ? '#c5cae9' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff'
                        }}>{busy ? '저장 중…' : '저장'}</button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
